/**
 * Aykah Motion — GSAP-powered micro-interactions & scroll animations
 *
 * Loads GSAP + ScrollTrigger from CDN, then wires up opt-in behaviors via
 * data-motion="<key>" attributes on Liquid sections. Wrapped in
 * gsap.matchMedia() so prefers-reduced-motion automatically disables animation.
 *
 * Existing CSS [data-reveal] fallback keeps working if this script fails to
 * load or the CDN is blocked.
 */
(function () {
  'use strict';

  if (window.__aykahMotionLoaded) return;
  window.__aykahMotionLoaded = true;

  /* GSAP + ScrollTrigger are self-hosted theme assets (assets/gsap.min.js,
     assets/scrolltrigger.min.js), loaded by deferred <script> tags in theme.liquid
     immediately before this file. Deferred scripts execute in document order, so both
     are already on window when this runs — no runtime CDN fetch, no busy-wait needed.
     init() no-ops safely if they are somehow unavailable; the CSS-based [data-reveal]
     fallback in scroll-reveal.js still covers non-GSAP reveals. */
  init();

  function init() {
    if (!window.gsap || !window.ScrollTrigger) return;
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    gsap.registerPlugin(ScrollTrigger);

    gsap.defaults({ duration: 0.6, ease: 'power2.out' });

    var mm = gsap.matchMedia();

    mm.add(
      {
        motionOk: '(prefers-reduced-motion: no-preference)',
        isDesktop: '(min-width: 1024px)'
      },
      function (ctx) {
        var motionOk = ctx.conditions.motionOk;
        var isDesktop = ctx.conditions.isDesktop;
        if (!motionOk) return;

        /* Editorial easing — feels deliberate and premium. Used across all
           reveals so the whole site has a consistent motion signature. */
        var EDITORIAL_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

        /* ── 1. SECTION REVEALS (upgrades [data-reveal] with GSAP stagger) ──
           Optional attrs: data-motion-delay="ms", data-motion-distance="px",
                           data-motion-blur="true" */
        gsap.utils.toArray('[data-motion="reveal"]').forEach(function (el) {
          var delay = (parseFloat(el.getAttribute('data-motion-delay')) || 0) / 1000;
          var dist = parseFloat(el.getAttribute('data-motion-distance'));
          if (isNaN(dist)) dist = 32;
          var useBlur = el.getAttribute('data-motion-blur') === 'true';
          var fromState = { opacity: 0, y: dist };
          if (useBlur) fromState.filter = 'blur(8px)';
          var toState = {
            opacity: 1,
            y: 0,
            duration: 1.1,
            delay: delay,
            ease: EDITORIAL_EASE,
            scrollTrigger: {
              trigger: el,
              start: 'top 88%',
              toggleActions: 'play none none none'
            }
          };
          if (useBlur) toState.filter = 'blur(0px)';
          gsap.fromTo(el, fromState, toState);
        });

        /* ── 2. STAGGER CHILDREN (product grids, card rows) ──
           Optional attrs: data-motion-child, data-motion-stagger="s",
                           data-motion-delay="ms", data-motion-distance="px" */
        gsap.utils.toArray('[data-motion="stagger"]').forEach(function (container) {
          var selector = container.getAttribute('data-motion-child') || ':scope > *';
          /* querySelectorAll rejects a leading combinator ("> .card" throws a
             SyntaxError), and an uncaught throw here would abort init() and kill
             every motion type declared after this block. Normalise the jQuery-style
             form to :scope, and never let one bad selector take the page down. */
          if (/^\s*[>+~]/.test(selector)) selector = ':scope ' + selector.trim();
          var children;
          try {
            children = container.querySelectorAll(selector);
          } catch (err) {
            return;
          }
          if (!children.length) return;
          var stagger = parseFloat(container.getAttribute('data-motion-stagger'));
          if (isNaN(stagger)) stagger = 0.12;
          var delay = (parseFloat(container.getAttribute('data-motion-delay')) || 0) / 1000;
          var dist = parseFloat(container.getAttribute('data-motion-distance'));
          if (isNaN(dist)) dist = 36;
          gsap.from(children, {
            opacity: 0,
            y: dist,
            duration: 0.95,
            delay: delay,
            ease: EDITORIAL_EASE,
            stagger: stagger,
            scrollTrigger: {
              trigger: container,
              start: 'top 85%',
              toggleActions: 'play none none none'
            }
          });
        });

        /* ── 3. HERO PARALLAX (subtle, desktop-only) ── */
        if (isDesktop) {
          gsap.utils.toArray('[data-motion="parallax"]').forEach(function (el) {
            var amount = parseFloat(el.getAttribute('data-motion-amount')) || 60;
            gsap.to(el, {
              yPercent: -amount / 10,
              ease: 'none',
              scrollTrigger: {
                trigger: el,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.8
              }
            });
          });
        }

        /* ── 4. PRODUCT CARD HOVER (image zoom + lift) ── */
        gsap.utils.toArray('[data-motion="card-hover"]').forEach(function (card) {
          var img = card.querySelector('img, [data-card-media]');
          var hoverTl = gsap.timeline({ paused: true });
          hoverTl.to(card, {
            y: -4,
            duration: 0.35,
            ease: 'power2.out'
          }, 0);
          if (img) hoverTl.to(img, { scale: 1.04, duration: 0.5, ease: 'power2.out' }, 0);

          card.addEventListener('mouseenter', function () { hoverTl.play(); });
          card.addEventListener('mouseleave', function () { hoverTl.reverse(); });
          card.addEventListener('focusin', function () { hoverTl.play(); });
          card.addEventListener('focusout', function () { hoverTl.reverse(); });
        });

        /* ── 5. BUTTON PRESS (small scale feedback) ── */
        gsap.utils.toArray('[data-motion="btn"]').forEach(function (btn) {
          btn.addEventListener('pointerdown', function () {
            gsap.to(btn, { scale: 0.97, duration: 0.12, ease: 'power2.out' });
          });
          btn.addEventListener('pointerup', function () {
            gsap.to(btn, { scale: 1, duration: 0.2, ease: 'back.out(2)' });
          });
          btn.addEventListener('pointerleave', function () {
            gsap.to(btn, { scale: 1, duration: 0.2, ease: 'power2.out' });
          });
        });

        /* ── 6a. SLIDE-IN (horizontal entrance for image-with-text, editorial splits)
           Optional attrs: data-motion-from="left|right" (default left),
                           data-motion-delay, data-motion-distance */
        gsap.utils.toArray('[data-motion="slide-in"]').forEach(function (el) {
          var from = (el.getAttribute('data-motion-from') || 'left').toLowerCase();
          var dist = parseFloat(el.getAttribute('data-motion-distance'));
          if (isNaN(dist)) dist = 64;
          var delay = (parseFloat(el.getAttribute('data-motion-delay')) || 0) / 1000;
          gsap.fromTo(el,
            { opacity: 0, x: from === 'right' ? dist : -dist },
            {
              opacity: 1,
              x: 0,
              duration: 1.2,
              delay: delay,
              ease: EDITORIAL_EASE,
              scrollTrigger: {
                trigger: el,
                start: 'top 88%',
                toggleActions: 'play none none none'
              }
            }
          );
        });

        /* ── 6b. SCRUB (scroll-linked translate for parallax layers, sticky reveals)
           Optional attrs: data-motion-scrub-y="px" (default 80 upward),
                           data-motion-scrub="true|false" (default true — smooth scrub) */
        if (isDesktop) {
          gsap.utils.toArray('[data-motion="scrub"]').forEach(function (el) {
            var y = parseFloat(el.getAttribute('data-motion-scrub-y'));
            if (isNaN(y)) y = -80;
            var scrub = el.getAttribute('data-motion-scrub');
            var scrubVal = (scrub === 'false') ? false : 0.8;
            gsap.to(el, {
              y: y,
              ease: 'none',
              scrollTrigger: {
                trigger: el,
                start: 'top bottom',
                end: 'bottom top',
                scrub: scrubVal
              }
            });
          });
        }

        /* ── 7. FADE-IN HEADINGS BY LINE/WORD ── */
        gsap.utils.toArray('[data-motion="headline"]').forEach(function (el) {
          var words = el.textContent.trim().split(/\s+/);
          el.textContent = '';
          var frag = document.createDocumentFragment();
          words.forEach(function (w, i) {
            var span = document.createElement('span');
            span.textContent = w;
            span.style.display = 'inline-block';
            span.style.willChange = 'transform, opacity';
            frag.appendChild(span);
            if (i < words.length - 1) frag.appendChild(document.createTextNode(' '));
          });
          el.appendChild(frag);
          gsap.fromTo(el.querySelectorAll('span'),
            { opacity: 0, y: 28 },
            {
              opacity: 1,
              y: 0,
              duration: 1.0,
              ease: EDITORIAL_EASE,
              stagger: 0.06,
              scrollTrigger: {
                trigger: el,
                start: 'top 90%',
                toggleActions: 'play none none none'
              }
            }
          );
        });

        return function () { /* matchMedia auto-reverts on teardown */ };
      }
    );

    /* Refresh ScrollTrigger after all images load (avoids mis-positioned triggers) */
    window.addEventListener('load', function () {
      ScrollTrigger.refresh();
    });
  }
})();
